import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-none border border-zinc-800 bg-void px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-[border-color] duration-300 focus:border-neon";

export function Field({ label, value, onChange, textarea = false, hint, testid }) {
    return (
        <div>
            <label className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{label}</label>
            {textarea ? (
                <textarea
                    data-testid={testid}
                    rows={3}
                    className={`${inputCls} resize-y`}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            ) : (
                <input
                    data-testid={testid}
                    className={inputCls}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}
            {hint && <p className="mt-1.5 font-mono text-[10px] text-zinc-600">{hint}</p>}
        </div>
    );
}

export function LinesField({ label, value, onChange, hint, testid }) {
    return (
        <Field
            label={label}
            textarea
            testid={testid}
            hint={hint || "One item per line"}
            value={Array.isArray(value) ? value.join("\n") : ""}
            onChange={(v) => onChange(v.split("\n").map((s) => s.trim()).filter(Boolean))}
        />
    );
}

export function CsvField({ label, value, onChange, testid }) {
    return (
        <Field
            label={label}
            testid={testid}
            hint="Separate with commas"
            value={Array.isArray(value) ? value.join(", ") : ""}
            onChange={(v) => onChange(v.split(",").map((s) => s.trim()).filter(Boolean))}
        />
    );
}

function ImageUpload({ label, value, onChange, testid }) {
    const { authHeaders } = useAuth();
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await axios.post(`${API}/uploads`, form, { headers: authHeaders() });
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error("Upload failed — try a smaller image");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div>
            <label className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{label}</label>
            <div className="flex flex-wrap items-center gap-4">
                {value ? (
                    <img src={value} alt="Current" className="h-16 w-28 border border-zinc-800 object-cover" />
                ) : null}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-file`} />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testid}-upload`}
                    className="flex items-center gap-2 border border-dashed border-zinc-700 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 transition-[border-color,color] duration-300 hover:border-neon hover:text-neon disabled:opacity-60"
                >
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {uploading ? "Uploading" : "Upload Image"}
                </button>
            </div>
            <input
                className={`${inputCls} mt-2`}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="/assets/... or uploaded image URL"
                data-testid={testid}
            />
        </div>
    );
}

function ItemField({ field, item, onChange, testid }) {
    const val = item[field.key];
    if (field.key === "image" || field.type === "image") {
        return <ImageUpload label={field.label} value={val} onChange={(v) => onChange(field.key, v)} testid={testid} />;
    }
    if (field.type === "lines") {
        return <LinesField label={field.label} value={val} onChange={(v) => onChange(field.key, v)} testid={testid} />;
    }
    if (field.type === "csv") {
        return <CsvField label={field.label} value={val} onChange={(v) => onChange(field.key, v)} testid={testid} />;
    }
    if (field.type === "checkbox") {
        return (
            <label className="flex items-center gap-3 pt-1 text-sm text-zinc-300">
                <input
                    type="checkbox"
                    data-testid={testid}
                    checked={!!val}
                    onChange={(e) => onChange(field.key, e.target.checked)}
                    className="h-4 w-4 accent-[#00F0FF]"
                />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{field.label}</span>
            </label>
        );
    }
    return (
        <Field
            label={field.label}
            value={val}
            onChange={(v) => onChange(field.key, v)}
            textarea={field.type === "textarea"}
            hint={field.hint}
            testid={testid}
        />
    );
}

export function ListEditor({ items, fields, onChange, addLabel = "Add Item", testidPrefix }) {
    const list = Array.isArray(items) ? items : [];
    const update = (i, key, v) => {
        const next = list.map((it, idx) => (idx === i ? { ...it, [key]: v } : it));
        onChange(next);
    };
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    const add = () => {
        const blank = {};
        fields.forEach((f) => {
            blank[f.key] = f.type === "lines" || f.type === "csv" ? [] : f.type === "checkbox" ? false : "";
        });
        onChange([...list, blank]);
    };

    return (
        <div className="space-y-4">
            {list.map((item, i) => (
                <div key={i} className="relative border border-zinc-800 bg-surface p-5" data-testid={`${testidPrefix}-item-${i}`}>
                    <button
                        onClick={() => remove(i)}
                        data-testid={`${testidPrefix}-remove-${i}`}
                        aria-label="Remove item"
                        className="absolute right-4 top-4 text-zinc-600 transition-colors duration-300 hover:text-red-400"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                    <p className="mb-4 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-600">
                        Item {String(i + 1).padStart(2, "0")}
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                        {fields.map((f) => (
                            <div key={f.key} className={f.type === "textarea" || f.type === "lines" ? "sm:col-span-2" : ""}>
                                <ItemField field={f} item={item} onChange={(k, v) => update(i, k, v)} testid={`${testidPrefix}-${f.key}-${i}`} />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <button
                onClick={add}
                data-testid={`${testidPrefix}-add`}
                className="flex items-center gap-2 border border-dashed border-zinc-700 px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 transition-[border-color,color] duration-300 hover:border-neon hover:text-neon"
            >
                <Plus className="h-4 w-4" />
                {addLabel}
            </button>
        </div>
    );
}
